import api from '@/api';

const CACHE_PREFIX = 'nested_select_cache_';
const MAX_PAGES = 500;

// ✅ защита от параллельных одинаковых загрузок
const inflight = new Map();

export async function getNestedSelectValues(propertyId) {
  // ✅ защита: если уже грузим этот propertyId — возвращаем тот же Promise
  if (inflight.has(propertyId)) return inflight.get(propertyId);

  const p = (async () => {
    const cacheKey = CACHE_PREFIX + propertyId;
    const cached = localStorage.getItem(cacheKey);
    const lastCheckedKey = `${cacheKey}_last_checked`;
    const lastChecked = localStorage.getItem(lastCheckedKey);

    const currentDate = new Date();
    const lastCheckedDate = lastChecked ? new Date(lastChecked) : null;
    const daysSinceLastCheck = lastCheckedDate
      ? (currentDate - lastCheckedDate) / (1000 * 60 * 60 * 24)
      : null;

    // ✅ лог без Infinity
    const needsCheck = !lastCheckedDate || daysSinceLastCheck > 7;
    if (needsCheck) {
      console.log(
        lastCheckedDate
          ? `⏳ Прошло ${Math.floor(daysSinceLastCheck)} дней — сверяем версию кэша.`
          : `⏳ Первая сверка кэша — проверяем версию.`,
      );
      return await checkAndUpdateCache(propertyId, cached, cacheKey, lastCheckedKey);
    }

    // Если кэш есть и сверка недавно была — возвращаем его
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        return parsed.results || [];
      } catch {
        // если кэш битый — перекачаем
        return await loadAllPages(propertyId, cacheKey, lastCheckedKey);
      }
    }

    // Если кэша нет вообще — грузим всё
    return await loadAllPages(propertyId, cacheKey, lastCheckedKey);
  })();

  inflight.set(propertyId, p);
  try {
    return await p;
  } finally {
    inflight.delete(propertyId);
  }
}

async function checkAndUpdateCache(propertyId, cached, cacheKey, lastCheckedKey) {
  if (cached) {
    let parsed;
    try {
      parsed = JSON.parse(cached);
    } catch {
      parsed = null;
    }

    const versionFromCache = parsed?.version;

    try {
      const response = await api.get(`/attributes/select_values/`, {
        params: { property: propertyId, include_children: true, page: 1 },
      });

      const versionFromServer = response.data?.version;

      if (versionFromServer && versionFromServer === versionFromCache) {
        localStorage.setItem(lastCheckedKey, new Date().toISOString());
        return parsed?.results || [];
      }
    } catch (e) {
      // если сверка упала — лучше вернуть кэш
      if (parsed?.results) return parsed.results;
    }
  }

  return await loadAllPages(propertyId, cacheKey, lastCheckedKey);
}

async function loadAllPages(propertyId, cacheKey, lastCheckedKey) {
  const fullResults = [];
  let page = 1;
  let version = null;

  let firstPageSize = null;
  let totalCount = null;

  let lastPageSignature = null;

  while (page <= MAX_PAGES) {
    // ✅ 404 не будет exception'ом
    const res = await api.get(`/attributes/select_values/`, {
      params: { property: propertyId, include_children: true, page },
      validateStatus: s => (s >= 200 && s < 300) || s === 404,
    });

    if (res.status === 404) {
      // ✅ это штатный конец страниц
      // (можно console.debug, чтобы не пугало)
      console.debug(`ℹ️ Page=${page} закончилась (404). stop.`);
      break;
    }

    const data = res.data || {};
    const results = Array.isArray(data.results) ? data.results : [];

    if (!version && data.version) version = data.version;
    if (typeof data.count === 'number') totalCount = data.count;

    if (firstPageSize === null) firstPageSize = results.length;

    const signature = results
      .slice(0, 5)
      .map(x => x?.id)
      .join(',');
    if (signature && signature === lastPageSignature) {
      console.warn(`⚠️ Похоже сервер вернул ту же страницу повторно. stop at page=${page}`);
      break;
    }
    lastPageSignature = signature;

    fullResults.push(...results);

    if (data.next) {
      page++;
      continue;
    }

    if (typeof totalCount === 'number') {
      if (fullResults.length >= totalCount) break;
      page++;
      continue;
    }

    if (!firstPageSize || results.length === 0 || results.length < firstPageSize) {
      break;
    }

    page++;
  }

  // ✅ НЕ сохраняем пустой кэш (чтобы не зафиксировать пустоту на 7 дней)
  if (fullResults.length > 0) {
    localStorage.setItem(cacheKey, JSON.stringify({ version, results: fullResults }));
    localStorage.setItem(lastCheckedKey, new Date().toISOString());
  } else {
    // если ничего не загрузили — lastChecked тоже не ставим
    console.warn(`⚠️ Ничего не загрузили для propertyId=${propertyId}. Кэш не обновляю.`);
  }

  return fullResults;
}
